import React, { Dispatch, HTMLAttributes, SetStateAction, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './FormInputs.module.scss';

// We provide two change handler interfaces for handling two scenarios:
//   onDebouncedChange -> To be used for calls (like API requests) that shouldn't happen until a user is
//     finished making their selection.
//   onChange -> To be called synchronously as the value of the input changes. Range is not a controlled
//     component, so use this if you need the current (sychronous) value of the input in the component
//     that is rendering it.
interface CustomRangeProps {
  defaultValue: number;
  onDebouncedChange?: (val: number) => void;
  onChange?: ((val: number) => void) | Dispatch<SetStateAction<number>>;
  // not clear why these aren't members of the HTMLInputElement interface
  min?: number;
  max: number;
  step?: number;
  disabled?: boolean;
}

type RangeProps = CustomRangeProps & Omit<HTMLAttributes<HTMLInputElement>, 'onChange'>;

export default function Range({
  defaultValue,
  onDebouncedChange,
  onChange,
  min,
  max,
  step,
  className,
  disabled,
  ...props
}: RangeProps) {
  const [value, setValue] = useState(defaultValue || 0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !onDebouncedChange) {
      return () => {};
    }

    const handler = () => onDebouncedChange(value);
    const timer = setTimeout(handler, 400);

    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value]);

  return (
    <input
      type="range"
      className={cn(styles.range, className, disabled && styles.disabled)}
      value={value}
      min={min || 0}
      max={max}
      step={step || 1}
      onChange={(e) => setValue(e.target.valueAsNumber)}
      disabled={disabled}
      {...props}
    />
  );
}
